import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import {
  AgentAvatar,
  AgentAvatarInput,
} from "~/pages/manifold-nextgen/components/agents/AgentAvatar";

export interface AgentAvatarStackProps {
  agents: AgentAvatarInput[];
  size?: number;
  max?: number;
  className?: string;
}

/** Overlapping agent avatars with one combined tooltip, for a session served by
 * more than one agent. Mirrors `AvatarStack`'s overlap/overflow DOM, but renders
 * the Dicebear `AgentAvatar` (matching the single-agent look) and merges every
 * name into a single tooltip instead of one per avatar. */
export function AgentAvatarStack({ agents, size = 18, max = 3, className }: AgentAvatarStackProps) {
  const shown = agents.slice(0, max);
  const overflow = agents.length - shown.length;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex shrink-0 items-center", className)}>
          {shown.map((agent, i) => (
            <AgentAvatar
              key={agent.id}
              agent={agent}
              size={size}
              className={cn("ring-2 ring-card", i > 0 && "-ml-1.5")}
              aria-hidden="true"
            />
          ))}
          {overflow > 0 && (
            <span
              className="relative -ml-1.5 inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-2 ring-card"
              style={{ width: size, height: size }}
            >
              +{overflow}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <ul className="space-y-0.5">
          {agents.map((agent) => (
            <li key={agent.id}>{agent.display_name}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
